import React, { useEffect, useMemo, useState } from 'react';
import { auditService } from '../../services/auditService';
import { organizationsService } from '../../services/organizationsService';
import { premisesService } from '../../services/premisesService';
import SearchableSelect from '../common/SearchableSelect';
import LoadingSpinner from '../common/LoadingSpinner';
import { usePageTitle } from '../../hooks/usePageTitle';
import './AuditTrailManagement.css';

const USER_TYPE_OPTIONS = [
    { id: '', name: 'All Admin Types' },
    { id: 'core_admin', name: 'Core Admin' },
    { id: 'client_admin', name: 'Organization Admin' },
    { id: 'app_admin', name: 'Premises Admin' }
];

const ACTION_OPTIONS = [
    { id: '', name: 'All Actions' },
    { id: 'CREATE', name: 'Create' },
    { id: 'UPDATE', name: 'Update' },
    { id: 'DELETE', name: 'Delete' },
    { id: 'STATUS_CHANGE', name: 'Status Change' },
    { id: 'ASSIGN', name: 'Assign' },
    { id: 'ASSIGN_BULK', name: 'Assign Bulk' },
    { id: 'UNASSIGN', name: 'Unassign' },
    { id: 'APPROVE', name: 'Approve' },
    { id: 'REJECT', name: 'Reject' },
    { id: 'LOGIN_SUCCESS', name: 'Login Success' },
    { id: 'LOGIN_FAILED', name: 'Login Failed' }
];

const formatDateTime = (value) => {
    if (!value) return '-';
    // DB returns UTC strings without timezone suffix; append Z so the browser
    // parses as UTC and toLocaleString() converts to the user's local time.
    const iso = value.includes('Z') || value.includes('+') ? value : value.replace(' ', 'T') + 'Z';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
};

const parseJsonValue = (value) => {
    if (!value) return null;
    if (typeof value === 'object') return value;
    try {
        return JSON.parse(value);
    } catch (error) {
        return null;
    }
};

const compactJson = (value) => {
    const parsed = parseJsonValue(value);
    if (!parsed) return '-';
    return JSON.stringify(parsed);
};

const AuditTrailManagement = () => {
    usePageTitle('Audit Trail');

    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 25,
        total: 0,
        total_pages: 0
    });

    const [filters, setFilters] = useState({
        action: '',
        table_name: '',
        actor_user_type: '',
        actor_user_id: '',
        org_id: '',
        premises_id: '',
        date_from: '',
        date_to: '',
        search: '',
        limit: 25
    });
    const [appliedFilters, setAppliedFilters] = useState(filters);
    const [organizations, setOrganizations] = useState([]);
    const [premises, setPremises] = useState([]);

    const organizationOptions = useMemo(() => {
        const opts = organizations.map((org) => ({
            id: String(org.id),
            name: org.name || org.organization_name || `Organization #${org.id}`
        }));
        return [{ id: '', name: 'All Organizations' }, ...opts];
    }, [organizations]);

    const premisesOptions = useMemo(() => {
        const scopedPremises = filters.org_id
            ? premises.filter((item) => String(item.org_id ?? item.organization_id ?? '') === String(filters.org_id))
            : premises;
        const opts = scopedPremises.map((item) => {
            const premisesName = item.premises_name || item.name || `Premises #${item.id}`;
            const orgName = item.organization_name || item.org_name || '';
            return {
                id: String(item.id),
                name: orgName ? `${premisesName} (${orgName})` : premisesName
            };
        });
        return [{ id: '', name: 'All Premises' }, ...opts];
    }, [premises, filters.org_id]);

    useEffect(() => {
        const loadReferenceData = async () => {
            try {
                const [orgResponse, premisesResponse] = await Promise.all([
                    organizationsService.getOrganizationsList().catch(() => []),
                    premisesService.getAllPremises({ page: 1, limit: 1000, search: '', organization_id: '', status: '' }).catch(() => ({ premises: [] }))
                ]);

                setOrganizations(Array.isArray(orgResponse) ? orgResponse : []);
                setPremises(Array.isArray(premisesResponse?.premises) ? premisesResponse.premises : []);
            } catch (err) {
                console.error('Failed to load audit reference data:', err);
            }
        };

        loadReferenceData();
    }, []);

    useEffect(() => {
        const loadAuditLogs = async () => {
            setLoading(true);
            setError('');
            try {
                const params = {
                    page: pagination.current_page,
                    limit: appliedFilters.limit,
                    action: appliedFilters.action || undefined,
                    table_name: appliedFilters.table_name || undefined,
                    actor_user_type: appliedFilters.actor_user_type || undefined,
                    actor_user_id: appliedFilters.actor_user_id || undefined,
                    org_id: appliedFilters.org_id || undefined,
                    premises_id: appliedFilters.premises_id || undefined,
                    date_from: appliedFilters.date_from || undefined,
                    date_to: appliedFilters.date_to || undefined,
                    search: appliedFilters.search || undefined
                };

                const response = await auditService.getAuditLogs(params);
                setLogs(Array.isArray(response?.logs) ? response.logs : []);
                setPagination((prev) => ({
                    ...prev,
                    current_page: response?.pagination?.current_page || prev.current_page,
                    per_page: response?.pagination?.per_page || prev.per_page,
                    total: response?.pagination?.total || 0,
                    total_pages: response?.pagination?.total_pages || 0
                }));
            } catch (err) {
                setError(err.message || 'Failed to load audit logs');
                setLogs([]);
            } finally {
                setLoading(false);
            }
        };

        loadAuditLogs();
    }, [pagination.current_page, appliedFilters]);

    const updateFilter = (key, value) => {
        setFilters((prev) => {
            const next = { ...prev, [key]: value };
            // A premises picked under a different organization is no longer
            // valid once the organization filter changes.
            if (key === 'org_id') {
                next.premises_id = '';
            }
            return next;
        });
    };

    const applyFilters = () => {
        setPagination((prev) => ({ ...prev, current_page: 1 }));
        setAppliedFilters({ ...filters });
    };

    const resetFilters = () => {
        const resetState = {
            action: '',
            table_name: '',
            actor_user_type: '',
            actor_user_id: '',
            org_id: '',
            premises_id: '',
            date_from: '',
            date_to: '',
            search: '',
            limit: 25
        };
        setFilters(resetState);
        setAppliedFilters(resetState);
        setPagination((prev) => ({ ...prev, current_page: 1 }));
    };

    const goToPage = (nextPage) => {
        if (nextPage < 1 || nextPage > (pagination.total_pages || 1)) return;
        setPagination((prev) => ({ ...prev, current_page: nextPage }));
    };

    return (
        <div className="audit-trail-page">
            <div className="audit-trail-container">
                <div className="audit-trail-header">
                    <div>
                        <h1>Admin Activity Audit Trail</h1>
                        <p>Track all data and operational changes made by Core, Organization, and Premises admins.</p>
                    </div>
                    <div className="audit-total">Total Logs: {pagination.total}</div>
                </div>

                <div className="audit-filters-card">
                    <div className="audit-filters-grid">
                        <div className="filter-item">
                            <label>Search</label>
                            <input
                                type="text"
                                value={filters.search}
                                onChange={(e) => updateFilter('search', e.target.value)}
                                placeholder="Actor, email, action, table or JSON content"
                            />
                        </div>

                        <div className="filter-item">
                            <label>Action</label>
                            <select value={filters.action} onChange={(e) => updateFilter('action', e.target.value)}>
                                {ACTION_OPTIONS.map((opt) => (
                                    <option key={opt.id || 'all'} value={opt.id}>{opt.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-item">
                            <label>Table Name</label>
                            <input
                                type="text"
                                value={filters.table_name}
                                onChange={(e) => updateFilter('table_name', e.target.value)}
                                placeholder="e.g. users, staff, premises"
                            />
                        </div>

                        <div className="filter-item">
                            <label>Admin Type</label>
                            <select value={filters.actor_user_type} onChange={(e) => updateFilter('actor_user_type', e.target.value)}>
                                {USER_TYPE_OPTIONS.map((opt) => (
                                    <option key={opt.id || 'all'} value={opt.id}>{opt.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-item">
                            <label>Actor User ID</label>
                            <input
                                type="number"
                                min="1"
                                value={filters.actor_user_id}
                                onChange={(e) => updateFilter('actor_user_id', e.target.value)}
                                placeholder="Exact user ID"
                            />
                        </div>

                        <div className="filter-item">
                            <label>Organization</label>
                            <SearchableSelect
                                options={organizationOptions}
                                value={String(filters.org_id || '')}
                                onChange={(value) => updateFilter('org_id', value)}
                                placeholder="All Organizations"
                                displayKey="name"
                                valueKey="id"
                                searchPlaceholder="Search organizations..."
                            />
                        </div>

                        <div className="filter-item">
                            <label>Premises</label>
                            <SearchableSelect
                                options={premisesOptions}
                                value={String(filters.premises_id || '')}
                                onChange={(value) => updateFilter('premises_id', value)}
                                placeholder="All Premises"
                                displayKey="name"
                                valueKey="id"
                                searchPlaceholder="Search premises..."
                            />
                        </div>

                        <div className="filter-item">
                            <label>Date From</label>
                            <input
                                type="date"
                                value={filters.date_from}
                                onChange={(e) => updateFilter('date_from', e.target.value)}
                            />
                        </div>

                        <div className="filter-item">
                            <label>Date To</label>
                            <input
                                type="date"
                                value={filters.date_to}
                                onChange={(e) => updateFilter('date_to', e.target.value)}
                            />
                        </div>

                        <div className="filter-item">
                            <label>Rows per page</label>
                            <select
                                value={filters.limit}
                                onChange={(e) => updateFilter('limit', Number(e.target.value))}
                            >
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                    </div>

                    <div className="audit-filters-actions">
                        <button className="btn-primary" onClick={applyFilters}>
                            <i className="fas fa-filter"></i>
                            Apply Filters
                        </button>
                        <button className="btn-secondary" onClick={resetFilters}>
                            <i className="fas fa-rotate-left"></i>
                            Reset
                        </button>
                    </div>
                </div>

                {error && <div className="audit-error">{error}</div>}

                <div className="audit-table-card">
                    {loading ? (
                        <div className="audit-loading">
                            <LoadingSpinner />
                            <span>Loading audit logs...</span>
                        </div>
                    ) : (
                        <div className="audit-table-wrap">
                            <table className="audit-table">
                                <thead>
                                    <tr>
                                        <th>Date/Time</th>
                                        <th>Actor</th>
                                        <th>Admin Type</th>
                                        <th>Action</th>
                                        <th>Table</th>
                                        <th>Record ID</th>
                                        <th>Organization</th>
                                        <th>Premises</th>
                                        <th>IP</th>
                                        <th>Before</th>
                                        <th>After</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.length === 0 ? (
                                        <tr>
                                            <td colSpan={11} className="empty-cell">No audit logs found for the selected criteria.</td>
                                        </tr>
                                    ) : (
                                        logs.map((log) => (
                                            <tr key={log.id}>
                                                <td>{formatDateTime(log.created_at)}</td>
                                                <td>
                                                    <div className="actor-block">
                                                        <strong>{log.actor_name || log.actor_username || log.actor_identifier || '-'}</strong>
                                                        <small>{log.actor_email || log.actor_identifier || '-'}</small>
                                                    </div>
                                                </td>
                                                <td>{log.actor_user_type || '-'}</td>
                                                <td><span className="action-pill">{log.action || '-'}</span></td>
                                                <td>{log.table_name || '-'}</td>
                                                <td>{log.record_id || '-'}</td>
                                                <td>{log.organization_id || '-'}</td>
                                                <td>{log.premises_id || '-'}</td>
                                                <td>{log.ip_address || '-'}</td>
                                                <td className="json-cell">{compactJson(log.old_values)}</td>
                                                <td className="json-cell">{compactJson(log.new_values)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="audit-pagination">
                    <button
                        className="btn-secondary"
                        onClick={() => goToPage(pagination.current_page - 1)}
                        disabled={pagination.current_page <= 1}
                    >
                        Previous
                    </button>
                    <span>
                        Page {pagination.current_page} of {Math.max(1, pagination.total_pages || 1)}
                    </span>
                    <button
                        className="btn-secondary"
                        onClick={() => goToPage(pagination.current_page + 1)}
                        disabled={pagination.current_page >= (pagination.total_pages || 1)}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AuditTrailManagement;
